import React from 'react';
import {
  Platform,
  StyleProp,
  StyleSheet,
  Text,
  TextStyle,
  useWindowDimensions,
  View,
  ViewStyle,
} from 'react-native';
import { displayFont, uiFont } from '../../styles/agapellaFonts';
import {
  DISCOVERY_MOBILE_BREAKPOINT,
  DISCOVERY_SHELL_MAX_WIDTH,
  discoveryHeroGeometry,
} from '../../screens/discovery/discoveryGridLayout';

export type DiscoveryHeroFrameProps = {
  testID: string;
  copyTestID?: string;
  title: string;
  subtitle: string;
  subtitleContent?: React.ReactNode;
  isDark: boolean;
  background: React.ReactNode;
  actions?: React.ReactNode;
  /** Keep the shared artwork height as a minimum, allowing wrapped copy to grow. */
  fitContent?: boolean;
  aside?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  copyStyle?: StyleProp<ViewStyle>;
  railStyle?: StyleProp<ViewStyle>;
  asideStyle?: StyleProp<ViewStyle>;
  titleStyle?: StyleProp<TextStyle>;
  subtitleStyle?: StyleProp<TextStyle>;
};

/**
 * Shared web hero frame for the three discovery entry pages.
 *
 * Artwork is page-specific, while image height, header overlap, copy rail,
 * typography and vertical copy origin are deliberately identical.
 */
export const DiscoveryHeroFrame: React.FC<DiscoveryHeroFrameProps> = ({
  testID,
  copyTestID,
  title,
  subtitle,
  subtitleContent,
  isDark,
  background,
  actions,
  fitContent = false,
  aside,
  style,
  copyStyle,
  railStyle,
  asideStyle,
  titleStyle,
  subtitleStyle,
}) => {
  const { width } = useWindowDimensions();
  const geometry = discoveryHeroGeometry(width);
  const mobile = width < DISCOVERY_MOBILE_BREAKPOINT;

  return (
    <View
      testID={testID}
      style={[
        styles.frame,
        fitContent ? { minHeight: geometry.height } : { height: geometry.height },
        style,
      ]}
    >
      <View pointerEvents="none" style={styles.background}>
        {background}
      </View>

      <View
        style={[
          styles.rail,
          fitContent && styles.railFitContent,
          {
            paddingHorizontal: geometry.gutter,
            paddingTop: geometry.copyTop,
          },
          railStyle,
        ]}
      >
        <View
          testID={copyTestID}
          style={[
            styles.copy,
            geometry.compact && styles.copyCompact,
            mobile && styles.copyMobile,
            copyStyle,
          ]}
        >
          <Text
            accessibilityRole="header"
            style={[
              styles.title,
              mobile && styles.titleMobile,
              { color: isDark ? '#FFF8F1' : '#3E2A22' },
              titleStyle,
            ]}
          >
            {title}
          </Text>
          {subtitleContent ?? (
            <Text
              style={[
                styles.subtitle,
                mobile && styles.subtitleMobile,
                { color: isDark ? '#E2D5CF' : '#67554C' },
                subtitleStyle,
              ]}
            >
              {subtitle}
            </Text>
          )}
          {actions ? <View style={[styles.actions, mobile && styles.actionsMobile]}>{actions}</View> : null}
        </View>

        {aside ? (
          <View
            style={[
              styles.aside,
              {
                right: geometry.gutter,
                top: geometry.headerInset + 18,
                height: geometry.height - geometry.headerInset - 18,
              },
              asideStyle,
            ]}
          >
            {aside}
          </View>
        ) : null}
      </View>
    </View>
  );
};

const WEB = Platform.OS === 'web';

const styles = StyleSheet.create({
  frame: {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
  },
  background: {
    ...StyleSheet.absoluteFillObject,
  },
  rail: {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    maxWidth: DISCOVERY_SHELL_MAX_WIDTH,
    height: '100%',
    alignSelf: 'center',
  },
  copy: {
    width: '50%',
    maxWidth: 630,
    minWidth: 0,
    position: 'relative',
    zIndex: 2,
  },
  railFitContent: {
    height: 'auto',
    paddingBottom: 32,
  },
  copyMobile: {
    maxWidth: 330,
  },
  copyCompact: {
    width: '100%',
    maxWidth: 630,
  },
  title: {
    ...displayFont(),
    fontSize: 56,
    lineHeight: 64,
    letterSpacing: -0.7,
    marginBottom: 12,
  } as any,
  titleMobile: {
    fontSize: 40,
    lineHeight: 48,
    letterSpacing: -0.5,
    marginBottom: 8,
  },
  subtitle: {
    ...uiFont(400),
    fontSize: 18,
    lineHeight: 28,
    maxWidth: 560,
  } as any,
  subtitleMobile: {
    fontSize: 16,
    lineHeight: 24,
    maxWidth: 320,
  },
  actions: {
    marginTop: 32,
  },
  actionsMobile: {
    marginTop: 24,
  },
  aside: {
    position: 'absolute',
    zIndex: 1,
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
    ...(WEB ? { transformOrigin: 'top right' } : null),
  } as any,
});
